import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Inbox, Send, Mail, User, Calendar, CheckCircle, XCircle, Clock, Check, X, Loader2, MailOpen 
} from 'lucide-react';
import { MailMessage, MailStatus } from '../types/mail';
import { useMailContext } from '../context/MailContext';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

interface MailListProps {
  className?: string;
}

export const MailList: React.FC<MailListProps> = ({ className = '' }) => {
  const { 
    incomingMails,
    outgoingMails,
    isLoading, 
    acceptMail, 
    rejectMail,
    loadMoreMail,
    hasMoreIncoming,
    hasMoreOutgoing,
    isLoadingMoreIncoming,
    isLoadingMoreOutgoing,
    unreadCount,
  } = useMailContext();
  
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'incoming' | 'outgoing'>('incoming');
  const [processingMail, setProcessingMail] = useState<string | null>(null);
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});

  const { ref: incomingRef, inView: inViewIncoming } = useInView({ threshold: 0.5 });
  const { ref: outgoingRef, inView: inViewOutgoing } = useInView({ threshold: 0.5 });

  useEffect(() => {
    if (inViewIncoming && hasMoreIncoming && !isLoadingMoreIncoming) {
      loadMoreMail('incoming');
    }
  }, [inViewIncoming, hasMoreIncoming, isLoadingMoreIncoming, loadMoreMail]);

  useEffect(() => {
    if (inViewOutgoing && hasMoreOutgoing && !isLoadingMoreOutgoing) {
      loadMoreMail('outgoing');
    }
  }, [inViewOutgoing, hasMoreOutgoing, isLoadingMoreOutgoing, loadMoreMail]);


  const handleAcceptMail = async (mailId: string) => {
    setProcessingMail(mailId);
    // Убираем предыдущее сообщение об ошибке для этого письма
    setErrorMessages(prev => {
      const newMessages = { ...prev };
      delete newMessages[mailId];
      return newMessages;
    });
    
    try {
      await acceptMail(mailId);
    } catch (error: any) {
      // Показываем ошибку пользователю в письме
      const errorMessage = error.response?.data?.message || error.message || 'Произошла ошибка при принятии письма';
      setErrorMessages(prev => ({
        ...prev,
        [mailId]: errorMessage
      }));
      console.error('Ошибка при принятии письма:', error);
    } finally {
      setProcessingMail(null);
    }
  };

  const handleRejectMail = async (mailId: string) => {
    setProcessingMail(mailId);
    try {
      await rejectMail(mailId);
    } finally {
      setProcessingMail(null);
    }
  };

  const handleOpenDocument = (mail: MailMessage) => {
    // Открываем документ только если письмо принято
    if (mail.status !== MailStatus.Accepted) {
      return;
    }

    // Проверяем, что documentId существует
    if (!mail.documentId || mail.documentId === 'undefined') {
      console.error('DocumentId не найден в письме:', mail);
      return;
    }

    // Переходим к документу в базе знаний
    navigate(`/knowledge/${mail.documentId}`);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Неизвестно';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Неизвестно';
      return date.toLocaleDateString('ru-RU', { 
        day: '2-digit', month: '2-digit', year: 'numeric' 
      }) + ' ' + date.toLocaleTimeString('ru-RU', { 
        hour: '2-digit', minute: '2-digit' 
      });
    } catch (error) {
      return 'Неизвестно';
    }
  };

  const getStatusIcon = (status: MailStatus) => {
    switch (status) {
      case MailStatus.Sent:
        return <Clock className="w-4 h-4 text-blue-500" />;
      case MailStatus.Accepted:
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case MailStatus.Declined:
        return <XCircle className="w-4 h-4 text-red-500" />;
      case MailStatus.Unavailable:
        return <XCircle className="w-4 h-4 text-orange-500" />;
      default:
        return <Mail className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: MailStatus) => {
    switch (status) {
      case MailStatus.Sent: return 'Отправлено';
      case MailStatus.Accepted: return 'Принято';
      case MailStatus.Declined: return 'Отклонено';
      case MailStatus.Unavailable: return 'Недоступно';
      default: return `Неизвестно`;
    }
  };

  const getSenderName = (mail: MailMessage) => mail.senderName || mail.senderUsername || 'Неизвестно';
  const getRecipientName = (mail: MailMessage) => mail.recipientName || mail.recipientUsername || 'Неизвестно';

  const pendingIncomingMailsCount = unreadCount;

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="flex items-center space-x-2">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-600 dark:text-gray-400">Загрузка почты...</span>
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-transparent overflow-x-hidden ${className}`}>
      {/* Заголовок с табами */}
      <div className="p-6 pb-0">
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gray-100 dark:bg-gray-700 p-1 rounded-xl shadow-inner">
            <div className="flex space-x-1">
              <button
                onClick={() => setActiveTab('incoming')}
                className={`relative flex items-center justify-center space-x-2 px-6 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  activeTab === 'incoming'
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Inbox className="w-4 h-4" />
                <span>Входящие</span>
                {pendingIncomingMailsCount > 0 && (
                  <span className={`text-xs rounded-full px-2 py-0.5 min-w-[1.25rem] h-5 flex items-center justify-center ${
                    activeTab === 'incoming' 
                      ? 'bg-white/20 text-white' 
                      : 'bg-blue-600 text-white'
                  }`}>
                    {pendingIncomingMailsCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('outgoing')}
                className={`relative flex items-center justify-center space-x-2 px-6 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  activeTab === 'outgoing'
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Send className="w-4 h-4" />
                <span>Исходящие</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Список писем */}
      <div className="px-6 pb-6">
        {activeTab === 'incoming' ? (
          incomingMails.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-gray-500 dark:text-gray-400">
              <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-6 mb-4">
                <Inbox className="w-12 h-12 opacity-50" />
              </div>
              <p className="text-base font-medium">Нет входящих писем</p>
              <p className="text-sm mt-1 opacity-75">Когда вам отправят документы, они появятся здесь</p>
            </div>
          ) : (
            <div className="space-y-4">
              {incomingMails.map((mail: MailMessage) => (
                <motion.div
                  key={mail.id}
                  className={`bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:scale-[1.02] ${
                    mail.status === MailStatus.Accepted ? 'cursor-pointer' : ''
                  }`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOpenDocument(mail);
                  }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-2 flex-shrink-0 hidden sm:block">
                      <MailOpen className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      
                      
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>
                            {mail.shareType === 'ShareAccess' ? 'Получен доступ к документу:' : 'Получен документ:'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1.5 flex-shrink-0">
                          {getStatusIcon(mail.status)}
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            {getStatusText(mail.status)}
                          </span>
                        </div>
                      </div>
                      
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                        {mail.documentTitle}
                      </h3>

                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2 overflow-hidden">
                        <div className="flex items-center space-x-2 min-w-0 flex-shrink">
                          <User className="w-3.5 h-3.5 flex-shrink-0 hidden sm:block" />
                          <span className="truncate">от {getSenderName(mail)}</span>
                        </div>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <Calendar className="w-3.5 h-3.5 hidden sm:block" />
                          <span className="whitespace-nowrap">{formatDate(mail.createdAt)}</span>
                        </div>
                      </div>
                      
                      {mail.text && (
                        <>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Сообщение:
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 p-2 bg-gray-50 dark:bg-gray-900/50 rounded">
                            {mail.text}
                          </p>
                        </>
                      )}
                      
                      {mail.status === MailStatus.Accepted && (
                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-2 font-medium">
                          Нажмите на письмо, чтобы открыть документ
                        </div>
                      )}
                      
                      {mail.status === MailStatus.Unavailable && (
                        <div className="text-xs text-orange-600 dark:text-orange-400 mt-2 font-medium">
                          ⚠️ Документ больше недоступен. Отправитель мог удалить его.
                        </div>
                      )}
                      
                      {errorMessages[mail.id] && (
                        <div className="text-xs text-red-600 dark:text-red-400 mt-2 font-medium bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800">
                          ❌ {errorMessages[mail.id]}
                        </div>
                      )}
                      
                      {mail.status === MailStatus.Sent && (
                        <div className="flex space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptMail(mail.id);
                            }}
                            disabled={processingMail === mail.id}
                            className="flex items-center space-x-2 px-4 py-2.5 text-sm font-medium text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {processingMail === mail.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                            <span>Принять</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRejectMail(mail.id);
                            }}
                            disabled={processingMail === mail.id}
                            className="flex items-center space-x-2 px-4 py-2.5 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {processingMail === mail.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                            <span>Отклонить</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
              {hasMoreIncoming && (
                <div ref={incomingRef} className="flex justify-center items-center p-4">
                  {isLoadingMoreIncoming && <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />}
                </div>
              )}
            </div>
          )
        ) : (
          outgoingMails.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-gray-500 dark:text-gray-400">
              <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-6 mb-4">
                <Send className="w-12 h-12 opacity-50" />
              </div>
              <p className="text-base font-medium">Нет исходящих писем</p>
              <p className="text-sm mt-1 opacity-75">Отправленные документы будут отображаться здесь</p>
            </div>
          ) : (
            <div className="space-y-4">
              {outgoingMails.map((mail: MailMessage) => (
                <motion.div
                  key={mail.id}
                  className={`bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:scale-[1.02] ${
                    mail.status === MailStatus.Accepted ? 'cursor-pointer' : ''
                  }`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOpenDocument(mail);
                  }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-2 flex-shrink-0 hidden sm:block">
                      <Send className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      
                      
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>
                            {mail.shareType === 'ShareAccess' ? 'Предоставлен доступ к документу:' : 'Отправлен документ:'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1.5 flex-shrink-0">
                          {getStatusIcon(mail.status)}
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            {getStatusText(mail.status)}
                          </span>
                        </div>
                      </div>

                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                        {mail.documentTitle}
                      </h3>

                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2 overflow-hidden">
                        <div className="flex items-center space-x-2 min-w-0 flex-shrink">
                          <User className="w-3.5 h-3.5 flex-shrink-0 hidden sm:block" />
                          <span className="truncate">для {getRecipientName(mail)}</span>
                        </div>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <Calendar className="w-3.5 h-3.5 hidden sm:block" />
                          <span className="whitespace-nowrap">{formatDate(mail.createdAt)}</span>
                        </div>
                      </div>
                      
                      
                      
                      {mail.text && (
                        <>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Сообщение:
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 p-2 bg-gray-50 dark:bg-gray-900/50 rounded">
                            {mail.text}
                          </p>
                        </>
                      )}
                      
                      {mail.status === MailStatus.Accepted && (
                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-2 font-medium">
                          Нажмите на письмо, чтобы открыть документ
                        </div>
                      )}
                      
                      {mail.status === MailStatus.Unavailable && (
                        <div className="text-xs text-orange-600 dark:text-orange-400 mt-2 font-medium">
                          ⚠️ Документ больше недоступен. Отправитель мог удалить его.
                        </div>
                      )}
                      
                      {errorMessages[mail.id] && (
                        <div className="text-xs text-red-600 dark:text-red-400 mt-2 font-medium bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800">
                          ❌ {errorMessages[mail.id]}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
              {hasMoreOutgoing && (
                <div ref={outgoingRef} className="flex justify-center items-center p-4">
                  {isLoadingMoreOutgoing && <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />}
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}; 